/**
 * Rendu Markdown, restreint à ce que les documents du dépôt utilisent réellement
 * (odoo-5133) : titres, paragraphes, gras, code, liens, listes, tableaux, blocs de code,
 * citations et filets.
 *
 * Pourquoi pas une bibliothèque : la source est écrite par nous, versionnée à côté du code,
 * et ce sous-ensemble tient en une page testée. Une dépendance de plus se paierait à chaque
 * audit et à chaque montée de version, pour rendre une syntaxe que nos documents n'emploient
 * pas. Si un jour ils l'emploient, le test le dira avant l'écran.
 *
 * Le HTML produit reste passé au désinfectant d'Angular par `[innerHTML]` : on ne contourne
 * pas la sécurité sous prétexte que le contenu vient de nous.
 */

/** Un titre du document, pour la table des matières et l'ancrage. */
export interface MarkdownHeading {
	level: number;
	text: string;
	id: string;
}

export interface RenderedMarkdown {
	html: string;
	headings: MarkdownHeading[];
}

function escaper(texte: string): string {
	return texte
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * Identifiant d'ancre stable pour un titre : sans accent, sans ponctuation, mots liés par
 * des tirets. Stable veut dire « le même à chaque rendu », pour qu'un lien partagé vers une
 * section survive au rechargement.
 */
export function ancre(texte: string): string {
	return texte
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 80);
}

/**
 * Transformations en ligne. L'échappement passe en premier — sinon un `<` du texte
 * ouvrirait une balise — puis le code, dont le contenu ne doit plus rien subir, est mis de
 * côté le temps que le gras et les liens soient traités.
 */
function enLigne(texte: string): string {
	const codes: string[] = [];
	let t = escaper(texte).replace(/`([^`]+)`/g, (_, code: string) => {
		codes.push(code);
		// Sentinelle prise dans la zone a usage prive d'Unicode : elle ne peut pas figurer
		// dans nos documents, et un caractere de controle ferait de ce fichier un binaire
		// aux yeux de git comme une alerte aux yeux du linter.
		return `\uE000${codes.length - 1}\uE000`;
	});

	t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
	t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, libelle: string, cible: string) => {
		const externe = /^https?:/i.test(cible);
		const attrs = externe ? ' target="_blank" rel="noopener noreferrer"' : '';
		return `<a href="${cible}"${attrs}>${libelle}</a>`;
	});

	return t.replace(/\uE000(\d+)\uE000/g, (_, i: string) => `<code>${codes[Number(i)]}</code>`);
}

function cellules(ligne: string): string[] {
	return ligne
		.replace(/^\s*\|/, '')
		.replace(/\|\s*$/, '')
		.split('|')
		.map((c) => c.trim());
}

const SEPARATEUR_TABLEAU = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

/** Rend un document Markdown en HTML, et remonte ses titres. */
export function renderMarkdown(source: string): RenderedMarkdown {
	const lignes = source.replace(/\r\n/g, '\n').split('\n');
	const html: string[] = [];
	const headings: MarkdownHeading[] = [];
	let i = 0;

	while (i < lignes.length) {
		const ligne = lignes[i];

		// Ligne vide
		if (!ligne.trim()) {
			i++;
			continue;
		}

		// Bloc de code encadré
		if (/^```/.test(ligne.trim())) {
			const contenu: string[] = [];
			i++;
			while (i < lignes.length && !/^```/.test(lignes[i].trim())) {
				contenu.push(lignes[i]);
				i++;
			}
			i++; // la clôture
			html.push(`<pre><code>${escaper(contenu.join('\n'))}</code></pre>`);
			continue;
		}

		// Filet
		if (/^\s*---+\s*$/.test(ligne)) {
			html.push('<hr />');
			i++;
			continue;
		}

		// Titre
		const titre = /^(#{1,6})\s+(.*)$/.exec(ligne);
		if (titre) {
			const niveau = titre[1].length;
			const texte = titre[2].trim();
			const id = ancre(texte);
			headings.push({ level: niveau, text: texte, id });
			html.push(`<h${niveau} id="${id}">${enLigne(texte)}</h${niveau}>`);
			i++;
			continue;
		}

		// Tableau : une ligne de cellules suivie d'une ligne de séparation
		if (ligne.trim().startsWith('|') && i + 1 < lignes.length && SEPARATEUR_TABLEAU.test(lignes[i + 1])) {
			const entetes = cellules(ligne);
			i += 2;
			const corps: string[][] = [];
			while (i < lignes.length && lignes[i].trim().startsWith('|')) {
				corps.push(cellules(lignes[i]));
				i++;
			}
			const th = entetes.map((c) => `<th>${enLigne(c)}</th>`).join('');
			const trs = corps
				.map((r) => `<tr>${r.map((c) => `<td>${enLigne(c)}</td>`).join('')}</tr>`)
				.join('');
			// Le cadre porte le defilement : un `display: block` sur le tableau lui ferait
			// perdre sa grille (colonnes desalignees, bordures disparues).
			html.push(
				`<div class="doc-tableau"><table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`,
			);
			continue;
		}

		// Liste à puces : les lignes suivantes indentées prolongent le point courant.
		if (/^\s*[-*]\s+/.test(ligne)) {
			const points: string[] = [];
			while (i < lignes.length && (/^\s*[-*]\s+/.test(lignes[i]) || (/^\s{2,}\S/.test(lignes[i]) && points.length))) {
				if (/^\s*[-*]\s+/.test(lignes[i])) {
					points.push(lignes[i].replace(/^\s*[-*]\s+/, ''));
				} else {
					points[points.length - 1] += ' ' + lignes[i].trim();
				}
				i++;
			}
			html.push(`<ul>${points.map((p) => `<li>${enLigne(p)}</li>`).join('')}</ul>`);
			continue;
		}

		// Citation
		if (/^\s*>\s?/.test(ligne)) {
			const contenu: string[] = [];
			while (i < lignes.length && /^\s*>\s?/.test(lignes[i])) {
				contenu.push(lignes[i].replace(/^\s*>\s?/, ''));
				i++;
			}
			html.push(`<blockquote><p>${enLigne(contenu.join(' ').trim())}</p></blockquote>`);
			continue;
		}

		// Paragraphe : jusqu'à la ligne vide ou le début d'un autre bloc.
		const paragraphe: string[] = [];
		while (
			i < lignes.length &&
			lignes[i].trim() &&
			!/^(#{1,6})\s/.test(lignes[i]) &&
			!/^```/.test(lignes[i].trim()) &&
			!/^\s*---+\s*$/.test(lignes[i]) &&
			!/^\s*[-*]\s+/.test(lignes[i]) &&
			!/^\s*>\s?/.test(lignes[i]) &&
			!lignes[i].trim().startsWith('|')
		) {
			paragraphe.push(lignes[i].trim());
			i++;
		}
		if (paragraphe.length) {
			html.push(`<p>${enLigne(paragraphe.join(' '))}</p>`);
		} else {
			// Rien n'a été consommé (une ligne de tableau orpheline, par exemple) : on avance
			// pour ne pas boucler indéfiniment sur elle.
			i++;
		}
	}

	return { html: html.join('\n'), headings };
}
